import { useEffect, useState } from "react";
import { AlertCircle, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  PAYROLL_ITEM_REASON_MAX_LENGTH,
  PAYROLL_ITEM_REASON_MIN_LENGTH,
  PAYROLL_ITEM_REASON_PRESETS,
} from "@/utils/enums/payroll.enum";

interface IProps {
  open: boolean;
  isSaving: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
}

export const SaveReasonDialog = ({
  open,
  isSaving,
  onCancel,
  onConfirm,
}: IProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [reason, setReason] = useState("");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("");
      setTouched(false);
    }
  }, [open]);

  if (!open) return null;

  const trimmed = reason.trim();
  const tooShort = trimmed.length < PAYROLL_ITEM_REASON_MIN_LENGTH;
  const tooLong = trimmed.length > PAYROLL_ITEM_REASON_MAX_LENGTH;
  const error = touched
    ? tooShort
      ? `Reason must be at least ${PAYROLL_ITEM_REASON_MIN_LENGTH} characters`
      : tooLong
        ? `Reason cannot exceed ${PAYROLL_ITEM_REASON_MAX_LENGTH} characters`
        : null
    : null;

  const handleConfirm = () => {
    setTouched(true);
    if (tooShort || tooLong) return;
    onConfirm(trimmed);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-sm shadow-xl max-w-md w-full">
        <div className="flex items-start justify-between px-5 pt-5 pb-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-full bg-info-muted text-info">
              <AlertCircle size={20} />
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900">
                {t("company:payrolls.reason_required")}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">
                {t(
                  "company:payrolls.this_change_will_be_recorded_in_the_audit_trail",
                )}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        <div className="px-5 py-3">
          <div className="text-xs text-gray-600 mb-2">
            {t("company:payrolls.quick_select")}
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3">
            {PAYROLL_ITEM_REASON_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => {
                  setReason(p);
                  setTouched(false);
                }}
                className={`text-[11px] px-2 py-1 rounded-xs border transition-colors ${
                  reason === p
                    ? "bg-info-muted border-blue-300 text-info"
                    : "bg-gray-50 border-border text-gray-700 hover:bg-gray-100"
                }`}
              >
                {p}
              </button>
            ))}
          </div>

          <label className="block text-xs font-medium text-gray-700 mb-1">
            {t("common:shared_ui.reason")}
            <span className="text-red-500">*</span>
          </label>
          <textarea
            autoFocus
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            onBlur={() => setTouched(true)}
            disabled={isSaving}
            placeholder={t(
              "company:payrolls.describe_why_you_re_making_this_change",
            )}
            maxLength={PAYROLL_ITEM_REASON_MAX_LENGTH}
            rows={3}
            className={`w-full text-sm px-3 py-2 border rounded-xs resize-none focus:outline-hidden focus:ring-2 ${
              error
                ? "border-red-300 focus:ring-red-200"
                : "border-border focus:ring-blue-200"
            }`}
          />
          <div className="flex items-center justify-between mt-1">
            {error ? (
              <span className="text-[11px] text-red-600">{error}</span>
            ) : (
              <span className="text-[11px] text-gray-400">
                {t("company:payrolls.min_characters", {
                  count: PAYROLL_ITEM_REASON_MIN_LENGTH,
                })}
              </span>
            )}
            <span className="text-[11px] text-gray-400">
              {trimmed.length}/{PAYROLL_ITEM_REASON_MAX_LENGTH}
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-100">
          <Button variant="outline" onClick={onCancel} disabled={isSaving}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSaving || tooShort || tooLong}
          >
            {isSaving
              ? t("common:states.saving")
              : t("company:payrolls.save_changes")}
          </Button>
        </div>
      </div>
    </div>
  );
};
import { useTranslation } from "react-i18next";
